<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useBookingsStore } from '@/stores/bookings';
import { useTheme } from '@/composables/useTheme';
import CategoryIcon from '@/components/ui/CategoryIcon.vue';
import qLogo from '@/assets/letter-q.png';
import type { Category, Envelope } from '@/lib/types';

const auth = useAuthStore();
const bookings = useBookingsStore();
const route = useRoute();
const router = useRouter();

const { theme, toggle } = useTheme();
/** Names the action, not the state: a button's label is what it will do. */
const themeLabel = computed(() =>
  theme.value === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
);

/**
 * One flyout at a time — the desktop "Services" and account menus. On mobile the topbar is
 * brand-only: the bottom tab bar and the Profile hub carry navigation, so there is no sheet.
 */
const open = ref<'browse' | 'account' | null>(null);

const root = ref<HTMLElement | null>(null);
const browseButton = ref<HTMLButtonElement | null>(null);
const accountButton = ref<HTMLButtonElement | null>(null);

const categories = ref<Category[]>([]);
const categoriesFailed = ref(false);
let categoriesRequested = false;

/** Fetched the first time a menu that shows them opens, not on every page load. */
async function ensureCategories(): Promise<void> {
  if (categoriesRequested) return;
  categoriesRequested = true;
  categoriesFailed.value = false;
  try {
    categories.value = (await api.get<Envelope<Category[]>>('/categories')).data;
  } catch {
    // Leave the door open: the next tap on the menu retries.
    categoriesFailed.value = true;
    categoriesRequested = false;
  }
}

function toggleMenu(which: 'browse' | 'account'): void {
  open.value = open.value === which ? null : which;
  if (open.value === 'browse') void ensureCategories();
}

const close = (): void => {
  open.value = null;
};

/** Esc closes the open flyout and hands focus back to the button that opened it. */
function onEsc(): void {
  if (!open.value) return;
  const trigger = open.value === 'browse' ? browseButton.value : accountButton.value;
  close();
  trigger?.focus();
}

function onPointerDown(event: PointerEvent): void {
  if (open.value && root.value && !root.value.contains(event.target as Node)) close();
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown));

// Navigating is how every menu item "closes" itself.
watch(() => route.fullPath, close);

const initials = computed(() => {
  const user = auth.user;
  if (!user) return '';
  return `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase();
});

async function signOut(): Promise<void> {
  close();
  await auth.logout();
  await router.push({ name: 'home' });
}
</script>

<template>
  <header ref="root" class="nav" @keydown.esc="onEsc">
    <div class="nav__row">
      <RouterLink to="/" class="nav__brand">
        <img :src="qLogo" class="nav__mark" width="22" height="22" alt="" />
        <span class="nav__wordmark">ServiceAndGo</span>
      </RouterLink>

      <nav class="nav__primary nav__desktop" aria-label="Primary">
        <button
          ref="browseButton"
          class="nav__link nav__disclosure"
          type="button"
          :aria-expanded="open === 'browse'"
          aria-controls="nav-browse"
          @click="toggleMenu('browse')"
        >
          Services
          <svg class="nav__chev" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.25 6 7.75l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <RouterLink class="nav__link" to="/search">Search</RouterLink>
      </nav>

      <div class="nav__end">
        <button
          class="nav__icon nav__desktop"
          type="button"
          :aria-label="themeLabel"
          :title="themeLabel"
          @click="toggle"
        >
          <!-- Shows where the click leads, not where you are: a sun means "go light". -->
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <template v-if="theme === 'dark'">
              <circle cx="12" cy="12" r="4.2" />
              <path d="M12 2.6v2.2M12 19.2v2.2M4.2 12H2M22 12h-2.2M5.9 5.9 4.4 4.4M19.6 19.6l-1.5-1.5M18.1 5.9l1.5-1.5M4.4 19.6l1.5-1.5" />
            </template>
            <path v-else d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4Z" stroke-linejoin="round" />
          </svg>
        </button>

        <template v-if="auth.isAuthenticated">
          <RouterLink class="nav__link nav__desktop nav__link--badged" to="/bookings">
            Bookings
            <span v-if="bookings.pending > 0" class="nav__badge" :aria-label="`${bookings.pending} pending bookings`">
              {{ bookings.pending > 9 ? '9+' : bookings.pending }}
            </span>
          </RouterLink>
          <RouterLink class="nav__link nav__desktop" to="/favorites">Saved</RouterLink>
          <RouterLink class="nav__link nav__desktop" to="/notifications">Alerts</RouterLink>
          <button
            ref="accountButton"
            class="nav__avatar nav__desktop"
            type="button"
            aria-label="Account menu"
            :aria-expanded="open === 'account'"
            aria-controls="nav-account"
            @click="toggleMenu('account')"
          >
            <img v-if="auth.user?.avatarUrl" :src="auth.user.avatarUrl" alt="" />
            <span v-else aria-hidden="true">{{ initials }}</span>
          </button>
        </template>
        <template v-else>
          <!-- Desktop shows both; mobile keeps a single Sign in for conversion (Profile handles the rest). -->
          <RouterLink class="nav__link nav__mobile" to="/login">Sign in</RouterLink>
          <RouterLink class="nav__link nav__desktop" to="/login">Sign in</RouterLink>
          <RouterLink class="btn btn--primary nav__cta nav__desktop" to="/register">
            Get started
          </RouterLink>
        </template>
      </div>
    </div>

    <!-- The catalogue, not a menu of marketing pages: what the system offers, one tap deep. -->
    <div v-if="open === 'browse'" id="nav-browse" class="panel panel--browse">
      <div class="panel__head">
        <p class="eyebrow">What we offer</p>
        <p v-if="categories.length" class="panel__count">{{ categories.length }} categories</p>
      </div>

      <p v-if="categoriesFailed" class="panel__error">
        Couldn't load the categories. Close this menu and try again.
      </p>
      <div v-else class="panel__grid">
        <RouterLink
          v-for="cat in categories"
          :key="cat.id"
          class="panel__item"
          :to="{ name: 'search', query: { category: cat.slug } }"
        >
          <span class="panel__glyph"><CategoryIcon :name="cat.iconName" /></span>
          <span class="panel__name">{{ cat.name }}</span>
        </RouterLink>
      </div>

      <RouterLink class="panel__all" to="/search">
        Browse every service <span aria-hidden="true">→</span>
      </RouterLink>
    </div>

    <div v-if="open === 'account'" id="nav-account" class="panel panel--account">
      <p class="panel__who">
        <strong>{{ auth.user?.firstName }} {{ auth.user?.lastName }}</strong>
        <span class="panel__email">{{ auth.user?.email }}</span>
      </p>

      <nav class="panel__links" aria-label="Account">
        <RouterLink v-if="auth.isProvider" to="/provider">Dashboard</RouterLink>
        <RouterLink v-if="auth.isProvider" to="/provider/listings">Your listings</RouterLink>
        <RouterLink v-if="auth.isProvider" to="/provider/schedule">Your schedule</RouterLink>
        <RouterLink v-if="auth.isAdmin" to="/admin/verifications">Verification queue</RouterLink>
        <RouterLink v-if="auth.isAdmin" to="/admin/reports">Reports</RouterLink>
        <RouterLink v-if="auth.isAdmin" to="/admin/audit">Audit trail</RouterLink>
      </nav>

      <button class="panel__signout" type="button" @click="signOut">Sign out</button>
    </div>
  </header>
</template>

<style scoped>
.nav {
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid var(--border);
  /* Translucent only where it can actually blur; otherwise the nav would be see-through. */
  background: var(--bg);
}
@supports (backdrop-filter: blur(12px)) {
  .nav {
    background: color-mix(in srgb, var(--bg) 78%, transparent);
    backdrop-filter: blur(12px) saturate(1.4);
  }
}

/* Same inset as `.page`, so the brand sits flush over the page content.
   Lifted above the scrim, so the close button stays visible while the sheet is open. */
.nav__row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  max-width: var(--page);
  margin: 0 auto;
  padding: 0 var(--gap);
  min-height: var(--nav-h);
}

.nav__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  text-decoration: none;
  padding: 0.35rem 0;
  margin-right: 0.9rem;
}
.nav__mark { flex: none; border-radius: 6px; }

.nav__primary { display: flex; align-items: center; gap: 0.15rem; }
.nav__end { display: flex; align-items: center; gap: 0.35rem; margin-left: auto; }

.nav__link {
  appearance: none;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.94rem;
  color: var(--muted);
  text-decoration: none;
  padding: 0.45rem 0.7rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.nav__link:hover { color: var(--text); background: var(--surface-2); }
.nav__link.router-link-active { color: var(--text); font-weight: 600; }

.nav__link--badged { display: inline-flex; align-items: center; gap: 0.35rem; }
.nav__badge {
  display: grid;
  place-items: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: var(--radius-pill);
  background: var(--danger);
  color: #fff;
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1;
}

.nav__disclosure { display: inline-flex; align-items: center; gap: 0.3rem; }
.nav__chev { transition: transform 0.15s ease; }
.nav__disclosure[aria-expanded='true'] { color: var(--text); background: var(--surface-2); }
.nav__disclosure[aria-expanded='true'] .nav__chev { transform: rotate(180deg); }

.nav__icon {
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}
.nav__icon:hover { color: var(--text); background: var(--surface-2); border-color: var(--border); }
.nav__icon[aria-expanded='true'] { color: var(--text); background: var(--surface-2); border-color: var(--border); }

.nav__cta { min-height: 38px; padding: 0.35rem 0.95rem; font-size: 0.92rem; }

.nav__avatar {
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  margin-left: 0.15rem;
  padding: 0;
  border: 1px solid var(--border-strong);
  border-radius: 50%;
  overflow: hidden;
  background: var(--accent-weak);
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.nav__avatar:hover, .nav__avatar[aria-expanded='true'] { border-color: var(--accent); }
.nav__avatar img { width: 100%; height: 100%; object-fit: cover; }

/* ── Flyouts ─────────────────────────────────────────────────────────────── */
.panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 1rem;
  animation: drop 0.16s cubic-bezier(0.2, 0.7, 0.2, 1);
}
@keyframes drop {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
}

.panel--browse {
  /* Left edge of the page content: under the brand, whatever the viewport. */
  left: calc(max(0px, (100% - var(--page)) / 2) + var(--gap));
  width: min(600px, calc(100vw - 2 * var(--gap)));
}

.panel__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.15rem 0.55rem 0.75rem;
}
.panel__count { margin: 0; font-family: var(--font-mono); font-size: 0.72rem; color: var(--muted); }

.panel__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.15rem; }

.panel__item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.55rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: var(--text);
  transition: background-color 0.15s ease;
}
.panel__item:hover { background: var(--surface-2); }
.panel__name { font-size: 0.92rem; line-height: 1.25; }

.panel__glyph {
  display: grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  background: var(--accent-weak);
  color: var(--accent);
}
.panel__glyph :deep(.cat-icon) { width: 19px; height: 19px; }

.panel__all {
  display: block;
  margin-top: 0.65rem;
  padding: 0.65rem 0.55rem 0.15rem;
  border-top: 1px solid var(--border);
  color: var(--accent);
  font-size: 0.92rem;
  font-weight: 550;
  text-decoration: none;
}
.panel__all span { display: inline-block; transition: transform 0.15s ease; }
.panel__all:hover span { transform: translateX(3px); }

.panel__error { margin: 0.25rem 0.55rem 0.5rem; color: var(--muted); font-size: 0.92rem; }

.panel--account {
  right: calc(max(0px, (100% - var(--page)) / 2) + var(--gap));
  width: min(280px, calc(100vw - 2 * var(--gap)));
  padding: 0.5rem;
}

.panel__who {
  display: grid;
  gap: 0.1rem;
  margin: 0;
  padding: 0.6rem 0.7rem 0.75rem;
  border-bottom: 1px solid var(--border);
}
.panel__who strong { font-size: 0.95rem; }
.panel__email { color: var(--muted); font-size: 0.84rem; overflow: hidden; text-overflow: ellipsis; }

.panel__links { display: grid; padding: 0.4rem 0; }
.panel__links:empty { display: none; }
.panel__links a {
  padding: 0.5rem 0.7rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: var(--text);
  font-size: 0.94rem;
}
.panel__links a:hover { background: var(--surface-2); }

.panel__signout {
  appearance: none;
  width: 100%;
  text-align: left;
  border: 0;
  border-top: 1px solid var(--border);
  border-radius: 0 0 var(--radius) var(--radius);
  background: none;
  font: inherit;
  font-size: 0.94rem;
  color: var(--muted);
  padding: 0.7rem;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.panel__signout:hover { color: var(--danger); background: var(--surface-2); }

/* ── Responsive split ────────────────────────────────────────────────────── */
@media (max-width: 719px) {
  .nav__desktop { display: none; }
}
@media (min-width: 720px) {
  .nav__mobile { display: none; }
}

/* Down to 320px: the wordmark is what gives, clipped rather than removed
   so the link keeps its accessible name. */
@media (max-width: 359px) {
  .nav__wordmark {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .nav__chev, .panel__all span { transition: none; }
}
</style>
